import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { SharedModule } from '@src/app/shared/shared.module';
import { BankJourneyCommentsDrawerComponent } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/bank-journey-comments-drawer/bank-journey-comments-drawer.component';
import { DealBankJourneySummaryComponent } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey-summary/deal-bank-journey-summary.component';
import { DealBankJourneyTabComponent } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey-tab/deal-bank-journey-tab.component';
import { DealBankJourneyStore } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey.store';

/**
 * Visão **Jornada nos bancos** da tela Meus deals (card 3765, opção A do protótipo
 * `meus-deals-jornada-nos-bancos-de-todos-os-deals-card-3765`, escolhida pelo Breno em 28/10/2026): os bancos de
 * todos os deals de quem está vendo, no mesmo quadro de dentro do deal.
 *
 * - O dado é o `DealBankJourneyStore` provido pela tela Meus deals, em `setAllDeals()` (`GET partners/bank-journey`,
 *   mesma régua de visibilidade do quadro de deals). O seletor da faixa lê o mesmo store para a contagem.
 * - Filtros em memória (a resposta vem inteira): por deal (empresa e número), por banco e pela busca da faixa
 *   (`search`). O resumo por etapa e o quadro seguem os filtros; a contagem do seletor, não.
 * - Clicar num banco abre os comentários (o painel mostra a empresa e o deal); o nome da empresa no cartão abre o deal
 *   já na visão dos bancos.
 */
@Component({
  selector: 'app-my-deals-bank-journey',
  standalone: true,
  imports: [
    FormsModule,
    SharedModule,
    DealBankJourneySummaryComponent,
    DealBankJourneyTabComponent,
    BankJourneyCommentsDrawerComponent,
  ],
  templateUrl: './my-deals-bank-journey.component.html',
  styleUrl: './my-deals-bank-journey.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyDealsBankJourneyComponent {
  protected readonly store = inject(DealBankJourneyStore);

  /** Texto da busca da faixa do Meus deals. */
  readonly search = input('');

  protected readonly dealOptions = computed<Option[]>(() =>
    [...this.store.deals()]
      .sort((a, b) => a.companyName.localeCompare(b.companyName, 'pt-BR'))
      .map(deal => ({
        label: `${deal.companyName} · #${deal.sequentialId}`,
        value: deal.partnerDealId,
      }))
  );

  protected readonly bankOptions = computed<Option[]>(() =>
    [
      ...new Set(
        this.store
          .allCards()
          .map(card => card.bankName)
          .filter(name => !!name)
      ),
    ]
      .sort((a, b) => a!.localeCompare(b!, 'pt-BR'))
      .map(name => ({ label: name!, value: name! }))
  );

  constructor() {
    effect(() => this.store.textFilter.set(this.search()));
  }
}
